import { InventoryAsset, Product } from '../../../types';
import { NewLocationEntry } from '../../../Api/ServiceRequest/serviceRequestApi';
import { RJSFValidationError } from '@rjsf/utils';
import { buildRequestSchema } from './requestSchema';
import { CHANGE_REQUEST, DISCONNECT_REQUEST } from './serviceRequestModel';

export interface RequestValidityInput {
  accountSelectionMissing: boolean;
  requestType: string;
  productSelection: Product | null;
  details: string;
  selectedDisconnectInventories: InventoryAsset[];
  isP2PProduct: boolean;
  locationA: string | null;
  locationZ: string | null;
  newLocations: NewLocationEntry[];
  locationSelections: string[];
  showAssetSelection: boolean;
  selectedAsset: InventoryAsset | null;
  showAdvancedLayout: boolean;
  schemaFormErrors: RJSFValidationError[];
  schemaFormData: Record<string, unknown>;
  requestSchema: ReturnType<typeof buildRequestSchema>;
}

export const isRequestFormValid = ({
  accountSelectionMissing,
  requestType,
  productSelection,
  details,
  selectedDisconnectInventories,
  isP2PProduct,
  locationA,
  locationZ,
  newLocations,
  locationSelections,
  showAssetSelection,
  selectedAsset,
  showAdvancedLayout,
  schemaFormErrors,
  schemaFormData,
  requestSchema
}: RequestValidityInput): boolean => {
  if (accountSelectionMissing) return false;
  const hasRequestType = !!requestType;
  const hasProductSelection = !!productSelection;
  if (requestType === DISCONNECT_REQUEST) {
    const hasInventory = selectedDisconnectInventories.length > 0;
    return hasRequestType && hasProductSelection && hasInventory && !!details;
  }

  const hasLocationSelection =
    requestType === CHANGE_REQUEST ||
    (isP2PProduct
      ? locationA !== null || locationZ !== null || newLocations.length > 0
      : locationSelections.length > 0 ||
        (newLocations.length > 0 && newLocations.every((loc) => loc.address.length > 0)));

  if (showAssetSelection && !selectedAsset) return false;

  if (showAdvancedLayout) {
    const isSchemaFormValid = schemaFormErrors.length === 0;

    // RJSF may not have validated yet, so the required values are checked here as well.
    const requiredFields = requestSchema?.schema?.validation_schema?.required || [];
    const hasRequiredFieldValues = requiredFields.every((fieldName: string) => {
      const value = schemaFormData[fieldName];
      return value !== null && value !== undefined && value !== '';
    });

    return hasRequestType && hasProductSelection && hasLocationSelection && isSchemaFormValid && hasRequiredFieldValues;
  }

  return hasRequestType && hasProductSelection;
};
